// ══════════════════════════════════════════════════════════
// ENTITLEMENTS SERVICE
//
// Resuelve funcionalidades premium desde el backend PHP.
// Fallback: resuelve desde Supabase (plans + subscriptions).
//
// FRONTEND NO PUEDE:
// - Conceder acceso porque el frontend tenga un flag local.
// - Marcar features como enabled sin confirmación del backend.
// - Modificar plan_id, subscription_status, ni features del plan.
// ══════════════════════════════════════════════════════════

import { backendApiRequest } from '@/services/rfwBackend';
import { getSupabaseClient } from '@/services/supabase/client';
import type { BackendResponse } from '@/types/rfwBackend';
import type {
  EntitlementKey,
  Entitlement,
  EntitlementsMap,
  BackendEntitlementsResponse,
} from '@/types/entitlements';
import {
  ENTITLEMENT_DEFINITIONS,
  buildEmptyEntitlementsMap,
  buildEntitlementsMap,
} from '@/types/entitlements';
import { adminEntitlementsData } from '@/mocks/adminEntitlements';

function supabase() {
  return getSupabaseClient();
}

// ═══════════════ BACKEND API (PRIMARY) ═══════════════

/**
 * Obtener entitlements del usuario autenticado desde el backend PHP.
 * GET /api/v1/me/entitlements
 */
export async function getMyEntitlements(): Promise<EntitlementsMap> {
  try {
    const response = await backendApiRequest<BackendEntitlementsResponse>(
      '/api/v1/me/entitlements',
    );
    if (response.success && response.data?.entitlements) {
      return buildEntitlementsMap(response.data.entitlements);
    }
    throw new Error('Backend sin entitlements');
  } catch {
    // Fallback a resolución desde Supabase
    return resolveEntitlementsFromSupabase();
  }
}

/**
 * Obtener entitlements de una radio específica desde el backend PHP.
 * GET /api/v1/radios/{radioId}/entitlements
 */
export async function getRadioEntitlements(radioId: string): Promise<EntitlementsMap> {
  try {
    const response = await backendApiRequest<BackendEntitlementsResponse>(
      `/api/v1/radios/${radioId}/entitlements`,
    );
    if (response.success && response.data?.entitlements) {
      return buildEntitlementsMap(response.data.entitlements);
    }
    throw new Error('Backend sin entitlements');
  } catch {
    return resolveEntitlementsFromSupabase(radioId);
  }
}

// ═══════════════ SUPABASE FALLBACK ═══════════════

/**
 * Resolver entitlements desde Supabase (plans + subscriptions).
 * Lee plans.features (jsonb) y subscriptions para determinar qué features están activas.
 */
async function resolveEntitlementsFromSupabase(radioId?: string): Promise<EntitlementsMap> {
  try {
    // Obtener perfil para conocer organization_id
    const { data: profile, error: profileError } = await supabase()
      .from('profiles')
      .select('organization_id')
      .single();

    if (profileError || !profile?.organization_id) {
      return getMockFallback(radioId);
    }

    const orgId = profile.organization_id as string;

    // Obtener suscripción activa
    const { data: subscription, error: subError } = await supabase()
      .from('subscriptions')
      .select('plan_id, status')
      .eq('organization_id', orgId)
      .in('status', ['active', 'trial'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (subError || !subscription) {
      return getMockFallback(radioId);
    }

    // Obtener plan
    const { data: plan, error: planError } = await supabase()
      .from('plans')
      .select('features, slug')
      .eq('id', subscription.plan_id as string)
      .maybeSingle();

    if (planError || !plan) {
      return getMockFallback(radioId);
    }

    const features = (plan.features as Record<string, string | number | boolean>) || {};
    const map = buildEmptyEntitlementsMap();

    for (const def of ENTITLEMENT_DEFINITIONS) {
      const value = features[def.key];
      if (value !== undefined) {
        if (typeof value === 'boolean') {
          map[def.key] = { ...def, enabled: value, limit: null, limitUnit: null };
        } else if (typeof value === 'number') {
          map[def.key] = { ...def, enabled: value > 0, limit: value, limitUnit: resolveUnit(def.key) };
        } else if (typeof value === 'string') {
          map[def.key] = {
            ...def,
            enabled: value === 'true' || value === 'enabled' || value === 'yes',
            limit: null,
            limitUnit: null,
          };
        }
      }
    }

    // Resolver límites por nomenclatura
    resolveLimitNaming(map, features);

    return map;
  } catch {
    return getMockFallback(radioId);
  }
}

function resolveUnit(key: EntitlementKey): string | null {
  switch (key) {
    case 'extra_storage': return 'GB';
    case 'multiple_stations': return 'estaciones';
    case 'podcasts': return 'podcasts';
    case 'contests': return 'concursos';
    default: return null;
  }
}

function resolveLimitNaming(
  map: EntitlementsMap,
  features: Record<string, string | number | boolean>,
): void {
  const limitMap: Partial<Record<EntitlementKey, { key: string; unit: string }>> = {
    extra_storage: { key: 'storage_gb', unit: 'GB' },
    multiple_stations: { key: 'max_stations', unit: 'estaciones' },
    podcasts: { key: 'max_podcasts', unit: 'podcasts' },
    contests: { key: 'max_contests', unit: 'concursos' },
  };

  for (const [entKey, { key: limitKey, unit }] of Object.entries(limitMap)) {
    if (limitKey in features) {
      const raw = features[limitKey];
      const num = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN;
      if (!Number.isNaN(num) && num > 0) {
        map[entKey as EntitlementKey] = {
          ...map[entKey as EntitlementKey],
          enabled: true,
          limit: num,
          limitUnit: unit,
        };
      }
    }
  }
}

function getMockFallback(radioId?: string): EntitlementsMap {
  if (!radioId) {
    return buildEntitlementsMap(adminEntitlementsData.entitlements);
  }
  return buildEntitlementsMap(adminEntitlementsData.entitlements);
}

// ═══════════════ INDIVIDUAL ═══════════════

/**
 * Resolver una feature individual.
 */
export async function resolveEntitlement(key: EntitlementKey, radioId?: string): Promise<Entitlement> {
  const all = radioId
    ? await getRadioEntitlements(radioId)
    : await getMyEntitlements();
  return all[key];
}

/**
 * Verificar si una feature específica está habilitada.
 */
export async function hasEntitlement(key: EntitlementKey, radioId?: string): Promise<boolean> {
  const entitlement = await resolveEntitlement(key, radioId);
  return entitlement.enabled;
}